import { useEffect, useRef, useState } from 'react';
import { useWeatherStore } from '../store/weatherStore';
import { WeatherMap } from './WeatherMap';
import { isToday } from '../utils/calendar';
import { formatTime, formatDate } from '../utils/time';
import { getDesignTokens } from '../utils/designTokens';

interface WeatherWidgetProps {
  isDarkMode?: boolean;
  location?: string;
  variant?: 'default' | 'large' | 'compact';
}

export default function WeatherWidget({ 
  isDarkMode = false, 
  location = 'family',
  variant = 'default'
}: WeatherWidgetProps) {
  const { weather, isLoading, error, fetchWeather, lastFetch } = useWeatherStore();
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedMapType, setSelectedMapType] = useState<'temperature' | 'precipitation'>('temperature');
  const [isMapExpanded, setIsMapExpanded] = useState(true);
  const [isForecastExpanded, setIsForecastExpanded] = useState(true);
  const [isHourlyExpanded, setIsHourlyExpanded] = useState(true);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Initial fetch and setup auto-refresh
  useEffect(() => {
    fetchWeather(location);
    
    // Set up automatic refresh every 30 minutes (30 * 60 * 1000 ms)
    refreshIntervalRef.current = setInterval(() => {
      fetchWeather(location, true); // Force refresh bypassing cache
    }, 30 * 60 * 1000);

    // Cleanup interval on unmount or location change
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [location, fetchWeather]);

  // Update current time every minute to refresh "last updated" display
  useEffect(() => {
    const timeUpdateInterval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Update every minute

    return () => {
      clearInterval(timeUpdateInterval);
    };
  }, []);

  // Manual refresh handler
  const handleRefresh = () => {
    fetchWeather(location, true); // Force refresh
  };

  // Format last updated time
  const formatLastUpdated = (timestamp: number | null): string => {
    if (!timestamp) return '';
    const now = currentTime;
    const diffMs = now - timestamp;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    const updateDate = new Date(timestamp);
    const isTodayDate = isToday(updateDate);
    
    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `Updated ${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
    } else if (diffHours < 24) {
      return `Updated ${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (isTodayDate) {
      return `Updated today at ${formatTime(updateDate)}`;
    } else if (diffDays === 1) {
      return `Updated yesterday at ${formatTime(updateDate)}`;
    } else {
      return `Updated ${formatDate(updateDate, 'short')} at ${formatTime(updateDate)}`;
    }
  };

  const tokens = getDesignTokens(isDarkMode);
  const theme = {
    bg: tokens.card.bg,
    border: tokens.border.light,
    text: tokens.text.primary,
    textMuted: tokens.text.muted,
    textSecondary: tokens.text.tertiary,
    card: tokens.bg.secondary,
      };

  // Variant-specific styling
  const variantStyles = {
    default: {
      container: 'p-4',
      minHeight: 'min-h-[200px]',
      header: 'text-sm',
      temp: 'text-2xl',
      icon: 'w-12 h-12',
      forecastHeader: 'text-xs',
      forecastItem: 'p-2 min-h-[48px]',
      forecastIcon: 'w-8 h-8',
      forecastText: 'text-xs',
      mapHeight: 240,
    },
    large: {
      container: 'p-4 sm:p-5',
      minHeight: 'min-h-[300px]',
      header: 'text-sm',
      temp: 'text-3xl sm:text-4xl',
      icon: 'w-14 h-14 sm:w-16 sm:h-16',
      forecastHeader: 'text-xs',
      forecastItem: 'p-2 sm:p-3 min-h-[56px] sm:min-h-[60px]',
      forecastIcon: 'w-9 h-9 sm:w-10 sm:h-10',
      forecastText: 'text-xs',
      mapHeight: 240,
    },
    compact: {
      container: 'p-2 sm:p-3',
      minHeight: 'min-h-[80px] sm:min-h-[150px]',
      header: 'text-[10px] sm:text-xs',
      temp: 'text-base sm:text-xl',
      icon: 'w-6 h-6 sm:w-10 sm:h-10',
      forecastHeader: 'text-xs',
      forecastItem: 'p-2 min-h-[44px]',
      forecastIcon: 'w-7 h-7',
      forecastText: 'text-xs',
      mapHeight: 180,
    },
  };

  const styles = variantStyles[variant];

  // Get weather-based theme colors
  const getWeatherTheme = (condition: string) => {
    const conditionLower = condition.toLowerCase();
    
    // Sunny/Clear
    if (conditionLower.includes('sunny') || conditionLower.includes('clear')) {
      return {
        bgGradient: isDarkMode 
          ? 'from-yellow-900/30 to-orange-900/20' 
          : 'from-yellow-50 to-orange-50',
        borderColor: isDarkMode ? 'border-yellow-700/50' : 'border-yellow-300',
        accent: isDarkMode ? 'text-yellow-300' : 'text-yellow-600',
      };
    }
    
    // Rainy
    if (conditionLower.includes('rain') || conditionLower.includes('drizzle') || conditionLower.includes('shower')) {
      return {
        bgGradient: isDarkMode 
          ? 'from-blue-900/30 to-indigo-900/20' 
          : 'from-blue-50 to-indigo-50',
        borderColor: isDarkMode ? 'border-blue-700/50' : 'border-blue-300',
        accent: isDarkMode ? 'text-blue-300' : 'text-blue-600',
      };
    }
    
    // Cloudy
    if (conditionLower.includes('cloud') || conditionLower.includes('overcast')) {
      return {
        bgGradient: isDarkMode 
          ? 'from-gray-800/40 to-slate-800/30' 
          : 'from-gray-50 to-slate-50',
        borderColor: isDarkMode ? 'border-gray-600/50' : 'border-gray-300',
        accent: isDarkMode ? 'text-gray-300' : 'text-gray-600',
      };
    }
    
    // Snow
    if (conditionLower.includes('snow') || conditionLower.includes('sleet')) {
      return {
        bgGradient: isDarkMode 
          ? 'from-cyan-900/30 to-blue-900/20' 
          : 'from-cyan-50 to-blue-50',
        borderColor: isDarkMode ? 'border-cyan-700/50' : 'border-cyan-300',
        accent: isDarkMode ? 'text-cyan-300' : 'text-cyan-600',
      };
    }
    
    // Storm/Thunder
    if (conditionLower.includes('storm') || conditionLower.includes('thunder') || conditionLower.includes('lightning')) {
      return {
        bgGradient: isDarkMode 
          ? 'from-purple-900/40 to-indigo-900/30' 
          : 'from-purple-50 to-indigo-50',
        borderColor: isDarkMode ? 'border-purple-700/50' : 'border-purple-300',
        accent: isDarkMode ? 'text-purple-300' : 'text-purple-600',
      };
    }
    
    // Fog/Mist
    if (conditionLower.includes('fog') || conditionLower.includes('mist') || conditionLower.includes('haze')) {
      return {
        bgGradient: isDarkMode 
          ? 'from-gray-800/30 to-slate-800/20' 
          : 'from-gray-50 to-slate-50',
        borderColor: isDarkMode ? 'border-gray-600/50' : 'border-gray-300',
        accent: isDarkMode ? 'text-gray-300' : 'text-gray-500',
      };
    }
    
    // Default
    return {
      bgGradient: isDarkMode 
        ? 'from-gray-800/30 to-gray-700/20' 
        : 'from-gray-50 to-gray-100',
      borderColor: isDarkMode ? 'border-gray-700' : 'border-gray-200',
      accent: isDarkMode ? 'text-gray-300' : 'text-gray-600',
    };
  };

  const weatherTheme = weather ? getWeatherTheme(weather.current.condition) : null;

  if (isLoading && !weather) {
    return (
      <div className={`${theme.bg} ${theme.border} border rounded-landing ${styles.container} ${styles.minHeight} flex items-center justify-center`}>
        <div className={`${theme.textMuted} ${styles.forecastText}`}>Loading weather...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${theme.bg} ${theme.border} border rounded-landing ${styles.container} ${styles.minHeight}`}>
        <div className={`${theme.text} ${styles.header} font-semibold mb-2`}>Weather</div>
        <div className={`${theme.textMuted} ${styles.forecastText}`}>{error}</div>
      </div>
    );
  }

  if (!weather) {
    return null;
  }

  // Show 3-day forecast (free WeatherAPI plan: today + 2 days)
  const forecastDays = weather.forecast.slice(0, 3);
  
  // Get all 24 hours from today for hourly forecast
  const hourlyForecast = weather.forecast[0]?.hourly || [];
  const displayHours = hourlyForecast.slice(0, 24);
  
  // Find the current hour (closest hour to now)
  const now = new Date();
  let currentHourIndex = -1;
  for (let i = 0; i < displayHours.length; i++) {
    const hourDate = new Date(displayHours[i].time);
    const nextHourDate = i < displayHours.length - 1 
      ? new Date(displayHours[i + 1].time)
      : null;
    if (hourDate <= now && (!nextHourDate || nextHourDate > now)) {
      currentHourIndex = i;
      break;
    }
  }

  return (
    <div className={`bg-gradient-to-br ${weatherTheme?.bgGradient || theme.bg} ${weatherTheme?.borderColor || theme.border} border rounded-landing ${styles.container} ${variant === 'compact' ? 'h-auto' : 'h-full'} flex flex-col ${variant === 'compact' ? 'overflow-visible' : 'overflow-hidden'} transition-all duration-500`}>
      {variant !== 'compact' && (
      <div className={`${theme.text} ${styles.header} font-semibold mb-2 sm:mb-3 flex items-center justify-between flex-shrink-0`}>
        <span className="truncate text-xs sm:text-sm">
          {weather.location.name}
          {weather.location.region && `, ${weather.location.region}`}
        </span>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className={`${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-70 active:scale-95'} transition-all min-h-[44px] min-w-[44px] flex items-center justify-center rounded-landing p-1 sm:p-2 ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} flex-shrink-0 ml-2`}
          aria-label="Refresh weather"
          title="Refresh weather"
        >
          <svg
            className={`w-5 h-5 ${theme.text} ${isLoading ? 'animate-spin' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>
      )}

      {variant === 'compact' ? (
        /* Option 1: Horizontal Card Strip - Mobile Optimized */
        <div className="flex flex-col">
          <div className="flex items-center gap-2 px-2 py-1.5 sm:px-3 sm:py-2">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className={`${weatherTheme?.accent || theme.text} text-5xl sm:text-6xl font-light`}>
                {weather.current.temperature.fahrenheit}°
              </span>
              <img
                src={weather.current.icon}
                alt={weather.current.condition}
                className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm sm:text-base font-medium text-gray-900 truncate">
                  {weather.current.condition}
                </div>
                <div className="text-xs sm:text-sm text-gray-500 truncate">
                  {weather.location.name}{weather.location.region ? `, ${weather.location.region}` : ''}
                </div>
                {lastFetch && (
                  <div className="text-[10px] sm:text-xs text-gray-400 mt-0.5 truncate">
                    {formatLastUpdated(lastFetch)}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 sm:gap-3 text-xs sm:text-sm flex-shrink-0">
              {forecastDays.slice(0, 3).map((day, idx) => {
                const date = new Date(day.date + 'T00:00:00');
                const isTodayDate = isToday(date);
                const dayName = isTodayDate ? 'Today' : date.toLocaleDateString('en-US', { weekday: 'short' });
                return (
                  <div key={idx} className="text-center min-w-[40px] sm:min-w-[50px]">
                    <div className="font-semibold text-gray-900">{day.high.fahrenheit}°</div>
                    <div className="text-gray-500 text-[10px] sm:text-xs">{day.low.fahrenheit}°</div>
                    <div className="text-[10px] text-gray-600 mt-0.5 hidden sm:block">{dayName}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto space-y-2 sm:space-y-3">
          {/* Current Weather */}
          <div className={`${isDarkMode ? 'bg-black/20' : 'bg-white/60'} backdrop-blur-sm rounded-landing ${variant === 'large' ? 'p-3 sm:p-4' : 'p-3'} border ${isDarkMode ? 'border-white/10' : 'border-white/30'} shadow-sm`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 sm:gap-2.5">
                <img
                  src={weather.current.icon}
                  alt={weather.current.condition}
                  className={styles.icon}
                />
                <div>
                  <div className={`${weatherTheme?.accent || theme.text} ${styles.temp} font-bold`}>
                    {weather.current.temperature.fahrenheit}°F
                  </div>
                  <div className={`${weatherTheme?.accent || theme.textMuted} ${styles.forecastText} font-medium`}>
                    {weather.current.condition}
                  </div>
                </div>
              </div>
            </div>
            <div className={`flex flex-wrap gap-2 ${styles.forecastText}`}>
              <div className={`${theme.textSecondary}`}>
                <span className={theme.textMuted}>Humidity: </span>
                {weather.current.humidity}%
              </div>
              <div className={`${theme.textSecondary}`}>
                <span className={theme.textMuted}>Wind: </span>
                {weather.current.wind.speed} mph {weather.current.wind.direction}
              </div>
              {weather.current.airQuality && (
                <div className={`${theme.textSecondary}`}>
                  <span className={theme.textMuted}>Air Quality: </span>
                  <span className={weather.current.airQuality.usEpaIndex <= 1 ? 'text-green-500' : weather.current.airQuality.usEpaIndex <= 2 ? 'text-yellow-500' : weather.current.airQuality.usEpaIndex <= 3 ? 'text-orange-500' : 'text-red-500'}>
                    {weather.current.airQuality.usEpaIndex === 1 ? 'Good' : weather.current.airQuality.usEpaIndex === 2 ? 'Moderate' : weather.current.airQuality.usEpaIndex === 3 ? 'Unhealthy for Sensitive' : weather.current.airQuality.usEpaIndex === 4 ? 'Unhealthy' : weather.current.airQuality.usEpaIndex === 5 ? 'Very Unhealthy' : 'Hazardous'}
                  </span>
                </div>
              )}
            </div>
          </div>

      {/* Weather Alerts */}
      {weather.alerts && weather.alerts.length > 0 && (
        <div className="mb-3 space-y-2">
          {weather.alerts.map((alert, index) => (
            <div
              key={index}
              className={`${isDarkMode ? 'bg-red-900/30 border-red-700/50' : 'bg-red-50 border-red-200'} border rounded-landing p-2 ${styles.forecastText}`}
            >
              <div className={`${isDarkMode ? 'text-red-300' : 'text-red-800'} font-semibold mb-1 text-xs`}>
                ⚠️ {alert.headline}
              </div>
              <div className={`${isDarkMode ? 'text-red-200' : 'text-red-700'} text-xs`}>
                {alert.description}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 24-Hour Forecast - Horizontal Scroll (only show if hourly data is available) */}
      {displayHours.length > 0 && (
        <div>
          {/* 24-Hour Forecast Header with Toggle */}
          <button
            onClick={() => setIsHourlyExpanded(!isHourlyExpanded)}
            className={`w-full flex items-center justify-between ${theme.text} ${styles.forecastHeader} font-semibold mb-2 min-h-[44px] px-2 py-1 rounded-landing ${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100'} transition-colors`}
            aria-label={isHourlyExpanded ? 'Collapse 24-hour forecast' : 'Expand 24-hour forecast'}
          >
            <span>24-Hour Forecast</span>
            <svg
              className={`w-5 h-5 transition-transform ${isHourlyExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          
          {/* Collapsible Hourly Forecast Content */}
          {isHourlyExpanded && (
            <div className="overflow-x-auto pb-2 -mx-1 px-1">
              <div className="flex gap-2 min-w-max">
                {displayHours.map((hour, index) => {
                  const hourDate = new Date(hour.time);
                  const hourStr = hourDate.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
                  const isCurrentHour = currentHourIndex === index;
                  const isPast = hourDate < now;

                  return (
                    <div
                      key={hour.timeEpoch}
                      className={`${isDarkMode ? 'bg-black/15' : 'bg-white/50'} backdrop-blur-sm rounded-landing ${isCurrentHour ? 'p-2 min-w-[85px]' : 'p-1.5 min-w-[70px]'} flex flex-col items-center gap-0.5 touch-manipulation border ${isDarkMode ? 'border-white/5' : 'border-white/20'} shadow-sm ${isPast ? 'opacity-60' : ''} ${isCurrentHour ? isDarkMode ? `ring-2 ${tokens.border.accent}` : `ring-2 ${tokens.border.accent}` : ''}`}
                    >
                      <div className={`${theme.text} ${isCurrentHour ? 'text-sm font-semibold' : `${styles.forecastText} font-medium text-[10px]`}`} style={isCurrentHour ? { fontFamily: 'system-ui, -apple-system, sans-serif' } : {}}>
                        {isCurrentHour ? 'Now' : hourStr}
                      </div>
                      <img
                        src={hour.icon}
                        alt={hour.condition}
                        className={isCurrentHour ? 'w-8 h-8' : 'w-7 h-7'}
                      />
                      <div className={`${theme.text} ${isCurrentHour ? 'text-sm font-semibold' : `${styles.forecastText} font-semibold text-[10px]`}`} style={isCurrentHour ? { fontFamily: 'system-ui, -apple-system, sans-serif' } : {}}>
                        {hour.temperature.fahrenheit}°
                      </div>
                      {(hour.chanceOfRain > 0 || hour.chanceOfSnow > 0) && (
                        <div className={`${theme.textMuted} text-xs`}>
                          {hour.chanceOfRain > hour.chanceOfSnow ? `💧 ${hour.chanceOfRain}%` : `❄️ ${hour.chanceOfSnow}%`}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3-Day Forecast */}
      <div className={`space-y-2 ${variant === 'large' ? 'flex-1' : ''}`}>
        {/* 3-Day Forecast Header with Toggle */}
        <button
          onClick={() => setIsForecastExpanded(!isForecastExpanded)}
          className={`w-full flex items-center justify-between ${theme.text} ${styles.forecastHeader} font-semibold mb-2 min-h-[44px] px-2 py-1 rounded-landing ${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100'} transition-colors`}
          aria-label={isForecastExpanded ? 'Collapse 3-day forecast' : 'Expand 3-day forecast'}
        >
          <span>3-Day Forecast</span>
          <svg
            className={`w-5 h-5 transition-transform ${isForecastExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        
        {/* Collapsible Forecast Content */}
        {isForecastExpanded && (
          <div className="space-y-2">
            {forecastDays.map((day, index) => {
              // Parse the date string - WeatherAPI returns dates as YYYY-MM-DD
              const date = new Date(day.date + 'T00:00:00');
              const isTodayDate = isToday(date);
              const dayName = isTodayDate
                ? 'Today'
                : date.toLocaleDateString('en-US', { weekday: 'short' });

              return (
                <div
                  key={day.date}
                  className={`${isDarkMode ? 'bg-black/15' : 'bg-white/50'} backdrop-blur-sm rounded-landing ${styles.forecastItem} flex items-center justify-between touch-manipulation border ${isDarkMode ? 'border-white/5' : 'border-white/20'} shadow-sm hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className={`${theme.text} ${styles.forecastText} font-medium ${variant === 'large' ? 'min-w-[60px] sm:min-w-[65px]' : 'min-w-[60px]'}`}>
                      {dayName}
                    </div>
                    <img
                      src={day.icon}
                      alt={day.condition}
                      className={`${styles.forecastIcon} flex-shrink-0`}
                    />
                    <div className={`${theme.textSecondary} ${styles.forecastText} flex-1 truncate`}>
                      {day.condition}
                    </div>
                  </div>
                  <div className={`${theme.text} ${styles.forecastText} ${variant === 'large' ? 'text-sm sm:text-base' : 'text-sm'} font-semibold flex items-center gap-1.5`}>
                    <span className={theme.textMuted}>{day.low.fahrenheit}°</span>
                    <span>{day.high.fahrenheit}°</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

        {/* Weather Maps - Leaflet overlay on base map */}
        <div className="space-y-2">
          {/* Weather Maps Header with Toggle */}
          <button
            onClick={() => setIsMapExpanded(!isMapExpanded)}
            className={`w-full flex items-center justify-between ${theme.text} ${styles.forecastHeader} font-semibold mb-2 min-h-[44px] px-2 py-1 rounded-landing ${isDarkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-100'} transition-colors`}
            aria-label={isMapExpanded ? 'Collapse weather maps' : 'Expand weather maps'}
          >
            <span>Weather Maps</span>
            <svg
              className={`w-5 h-5 transition-transform ${isMapExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        
          {/* Collapsible Map Content */}
          {isMapExpanded && (
            <div
              className="space-y-2 overflow-y-auto"
              style={{ maxHeight: variant === 'large' ? 520 : 420 }}
            >
              {/* Map Type Selector - Compact */}
              <div className="grid grid-cols-2 gap-1.5 mb-2">
                {(['temperature', 'precipitation'] as const).map((mapType) => (
                  <button
                    key={mapType}
                    onClick={() => setSelectedMapType(mapType)}
                    className={`px-2 py-1.5 min-h-[40px] rounded-landing font-medium transition-colors capitalize text-[10px] ${
                      selectedMapType === mapType
                        ? `${tokens.button.primary} ${tokens.text.inverse}`
                        : `${tokens.button.secondary} ${tokens.text.primary}`
                    }`}
                  >
                    {mapType === 'temperature' ? '🌡️ Temp' : '🌧️ Precip'}
                  </button>
                ))}
              </div>

              {/* Map Display - Leaflet */}
              <div className="relative">
                <WeatherMap
                  latitude={weather.location.coordinates?.latitude || 0}
                  longitude={weather.location.coordinates?.longitude || 0}
                  mapType={selectedMapType}
                  isDarkMode={isDarkMode}
                  height={styles.mapHeight}
                />
              </div>

              {/* Legend for temperature layer (below map) */}
              {selectedMapType === 'temperature' && (
                <div className={`${isDarkMode ? 'bg-gray-900/95 text-gray-100 border border-white/20' : 'bg-white text-gray-900 border border-gray-200'} rounded-landing px-2 py-1.5 shadow-sm mt-2 w-full overflow-x-auto`}>
                  <div className="flex items-center gap-2 text-[9px] font-semibold whitespace-nowrap">
                    {[
                      { label: '<32', color: 'bg-blue-900' },
                      { label: '32-50', color: 'bg-blue-600' },
                      { label: '50-68', color: 'bg-green-500' },
                      { label: '68-80', color: 'bg-yellow-400 text-gray-900' },
                      { label: '80-95', color: 'bg-orange-500' },
                      { label: '95+', color: 'bg-red-600' },
                    ].map((band) => (
                      <div key={band.label} className="flex items-center gap-1">
                        <span className={`w-4 h-4 rounded-landing ${band.color} block`} />
                        <span>{band.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Last Updated Time */}
        {lastFetch && (
          <div className={`${theme.textMuted} text-xs text-center pt-2 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
            {formatLastUpdated(lastFetch)}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
